import React, { useEffect, useMemo, useRef, useState } from "react";
import { Cell, Pie, PieChart } from "recharts";
import { ChartTooltipV2, ChartTooltipV2Wrapper } from "@/components/ui/chartV2";
import { ChartDataPoint } from "@/types/chart";
import { useChartDimensionsV2 } from "@/hooks/useChartDimensions";
import LegendWrapperV2 from "./components/legendWrapperV2";
import { formatAxisValue } from "@/lib/utils";
import { generateColor } from "@/common/colorUtils";
import type { HandleLegendItemChangeArguments } from "@/types/report";
import { SeriesConfig, SeriesConfigItem } from "@/common/type/report-types";
import { ReportChartSettingsV2 } from "@/common/report-configuration";

interface PieChartComponentV2Props {
  data: ChartDataPoint[]; // Objects with 'name' and series keys
  xAxis: string; // Should be "name"
  xAxisLabel: string; // Not used for pie charts but kept for consistency
  yAxisLeftLabel: string; // Not used for pie charts but kept for consistency
  seriesConfig: SeriesConfig;
  chartSettings: ReportChartSettingsV2;
  maxHeight?: number;
  handleLegendItemChange: (args: HandleLegendItemChangeArguments) => void;
}

type PieDataItem = {
  name: string;
  value: number;
  color: string;
};

const PieChartComponentV2: React.FC<PieChartComponentV2Props> = ({
  data,
  xAxis,
  seriesConfig,
  chartSettings,
  maxHeight,
  handleLegendItemChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(800);

  // Measure container width on mount and resize
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        if (width > 0) {
          setContainerWidth(width);
        }
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const dimensions = useChartDimensionsV2(
    chartSettings,
    maxHeight,
    containerWidth,
  );

  // Transform data for pie chart
  // For pie charts, each row (category) becomes a slice
  // We sum all series values for that row to get the total slice size
  const pieData = useMemo((): PieDataItem[] => {
    if (!data?.length) return [];

    // Get all series keys (excluding system fields like name, _employees, etc)
    const seriesKeys = Array.from(seriesConfig.keys()).filter(
      (key) => !key.endsWith("_employees") && !key.endsWith("_projected"),
    );

    return data
      .map((row, index) => {
        // For each category (row), sum all series values
        const totalValue = seriesKeys.reduce((sum, key) => {
          const value = row[key];
          return sum + (typeof value === "number" ? value : 0);
        }, 0);

        const categoryName = row[xAxis] as string;

        // For pie charts, each slice (category) needs a unique color
        // Priority: 1) saved color from chartSettings, 2) generate from palette
        let color = chartSettings.legendItems[categoryName]?.color;
        if (!color) {
          color = generateColor(index);
        }

        return {
          name: categoryName,
          value: totalValue,
          color,
        };
      })
      .filter((item) => {
        // Filter out hidden items and zero values
        const isHidden = chartSettings.legendItems[item.name]?.hidden;
        return item.value > 0 && !isHidden;
      });
  }, [data, xAxis, seriesConfig, chartSettings.legendItems]);

  // Create legend items based on pie slices (categories), not series
  const pieSeriesConfig = useMemo((): SeriesConfig => {
    const result = new Map<string, SeriesConfigItem>();
    const measure = Array.from(seriesConfig.values())[0].measure;
    pieData.forEach((slice) => {
      result.set(slice.name, {
        key: slice.name,
        label: slice.name,
        color: slice.color,
        measure,
        axisId: measure.axisGroup,
        hidden: chartSettings.legendItems[slice.name]?.hidden || false,
        isProjected: false,
        tooltip: {
          showEmployeeCount: false,
          hasEmployeeData: false,
        },
      });
    });
    return result;
  }, [pieData, seriesConfig, chartSettings.legendItems]);

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        flexDirection: ["top", "bottom"].includes(
          chartSettings.legendPosition || "right",
        )
          ? "column"
          : "row",
        alignItems: "center",
        width: "100%",
        height: "100%",
      }}
    >
      {chartSettings.includeLegend &&
        ["left", "top"].includes(chartSettings.legendPosition || "right") && (
          <LegendWrapperV2
            position={chartSettings.legendPosition || "right"}
            items={pieSeriesConfig}
            onItemChange={handleLegendItemChange}
          />
        )}

      <div
        style={{
          flex: 1,
          minHeight: 0,
          height:
            chartSettings.legendPosition === "bottom"
              ? "calc(100% - 100px)"
              : "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <PieChart
          width={dimensions.chartWidth}
          height={dimensions.chartHeight}
          margin={dimensions.margin}
        >
          <Pie
            data={pieData}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={
              Math.min(dimensions.chartWidth, dimensions.chartHeight) * 0.35
            }
            label={(props) => {
              const entry = pieData[props.index];
              const formattedValue = chartSettings.showCount
                ? formatAxisValue(entry.value)
                : `${parseFloat(entry.value.toFixed(1))}%`;
              return `${entry.name} (${formattedValue})`;
            }}
          >
            {pieData.map((entry) => (
              <Cell key={`cell-${entry.name}`} fill={entry.color} />
            ))}
          </Pie>
          <ChartTooltipV2
            content={(props) => (
              <ChartTooltipV2Wrapper
                chartType="Pie"
                seriesConfig={pieSeriesConfig}
                showCount={chartSettings.showCount}
                {...props}
              />
            )}
          />
        </PieChart>
      </div>

      {chartSettings.includeLegend &&
        ["right", "bottom"].includes(
          chartSettings.legendPosition || "right",
        ) && (
          <LegendWrapperV2
            position={chartSettings.legendPosition || "right"}
            items={pieSeriesConfig}
            onItemChange={handleLegendItemChange}
          />
        )}
    </div>
  );
};

export default React.memo(PieChartComponentV2) as typeof PieChartComponentV2;
